import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table';
import { Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loading } from '@/components/common/Loading';
import { ColumnVisibility } from '@/components/common/ColumnVisibility';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { useColumnVisibility } from '@/lib/hooks/useColumnVisibility';
import { useTableZoom, type TableZoom } from '@/lib/hooks/useTableZoom';
import type { SettingsFilters } from '../types';

interface SettingsDataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  filters: SettingsFilters;
  updateFilter: <K extends keyof SettingsFilters>(key: K, value: SettingsFilters[K], resetPage?: boolean) => void;
  isLoading: boolean;
  isError?: boolean;
  error?: string;
  emptyMessage?: string;
  emptyDescription?: string;
  createButton?: React.ReactNode;
  extraFilters?: React.ReactNode;
  /** Unique id used to persist this catalog's column show/hide state separately. */
  tableId?: string;
  /** Page-owned zoom (rendered by the page's Pagination); omitted = own control inline. */
  tableZoom?: TableZoom;
}

export function SettingsDataTable<TData>({
  data,
  columns,
  filters,
  updateFilter,
  isLoading,
  isError,
  error,
  emptyMessage,
  emptyDescription,
  createButton,
  extraFilters,
  tableId = 'settings',
  tableZoom: tableZoomProp,
}: SettingsDataTableProps<TData>) {
  const { t } = useTranslation();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(`settings:${tableId}`);
  const internalTableZoom = useTableZoom();
  const tableZoom = tableZoomProp ?? internalTableZoom;
  const [rowSelection, setRowSelection] = React.useState({});

  // Debounced search - same pattern as TasksDataTable
  const [searchValue, setSearchValue] = React.useState(filters.s ?? '');
  const debouncedSearch = useDebounce(searchValue, 300);
  const isFirstRender = React.useRef(true);

  // Update filter when debounced search changes
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const trimmed = debouncedSearch.trim();
    const newValue = trimmed.length >= 2 ? trimmed : undefined;
    // Only update if value actually changed to avoid resetting page
    if (newValue !== filters.s) {
      updateFilter('s', newValue, true);
    }
  }, [debouncedSearch]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      columnVisibility,
      rowSelection,
    },
  });

  return (
    <div className="w-full">
      <div className="flex flex-col-reverse sm:flex-row items-center gap-x-2 gap-y-2 py-4 flex-wrap">
        <div className="relative max-w-sm min-w-32 ml-auto">
          <Input
            placeholder={t('search')}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="h-8 text-xs pr-10"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
        </div>

        {extraFilters}

        {!tableZoomProp && <TableZoomControls zoom={tableZoom} />}
        <ColumnVisibility table={table} />
      </div>

      <div className="border rounded-md">
        <Table style={{ zoom: tableZoom.zoom }}>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={99}>
                  <Loading className="w-full" />
                </TableCell>
              </TableRow>
            ) : isError ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-red-500">
                  {error || t('errorLoading')}
                </TableCell>
              </TableRow>
            ) : data?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 py-[100px] text-center">
                  <h1 className="text-xl font-medium mb-2">{emptyMessage || t('noDataFound')}</h1>
                  <p>{emptyDescription || t('trackDataToBetterManage')}</p>
                  {createButton}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
